import { useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'
import { Button } from './ui'

/** The red pulsing "On air" mark. */
export function OnAirBadge() {
  return (
    <span className="on-air">
      <span className="on-air-dot" aria-hidden="true" />
      On air
    </span>
  )
}

/** Plays a live HLS stream: natively where the browser can (Safari, iOS), with hls.js elsewhere. */
export function LivePlayer({ src, poster }: { src: string; poster?: string | null }) {
  const video = useRef<HTMLVideoElement>(null)
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const element = video.current
    if (!element) return
    setFailed(false)
    if (element.canPlayType('application/vnd.apple.mpegurl')) {
      element.src = src
      return () => {
        element.removeAttribute('src')
        element.load()
      }
    }
    let player: Hls | undefined
    let stopped = false
    void import('hls.js').then(({ default: HlsPlayer }) => {
      if (stopped) return
      if (!HlsPlayer.isSupported()) {
        setFailed(true)
        return
      }
      player = new HlsPlayer({ liveDurationInfinity: true })
      player.on(HlsPlayer.Events.ERROR, (_event, data) => {
        if (data.fatal) setFailed(true)
      })
      player.loadSource(src)
      player.attachMedia(element)
    })
    return () => {
      stopped = true
      player?.destroy()
    }
  }, [src, attempt])

  return (
    <div className="live-player stack-sm">
      {/* Muted so browsers allow it to start on its own; the viewer unmutes. */}
      <video ref={video} className="player-video" poster={poster ?? undefined} controls playsInline autoPlay muted data-testid="live-video" />
      {failed && (
        <div className="row">
          <span className="muted small">The stream stopped or could not be played here.</span>
          <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
        </div>
      )}
    </div>
  )
}
